import React, { useState, useEffect, useCallback } from 'react';
import * as Styles from './RetentionVpsOci.styles';
import useLocale from '@/hooks/useLocale';
import { Button, Skeleton } from 'gatorcomponents';

import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { vpsOciActions } from '@/redux/modules/productDetail/vpsOci';
import { getIconBySlug } from '@/pages/Products/VpsOciDetailPage/utils';
import ModalPanels from '@/pages/Products/VpsOciDetailPage/pages/ProductTemplatesPage/components/AvailableOptions/components/ModalPanels';
import SnappyImage from './media/vpsoci-retention-snappy.png';

import { useHistory } from 'react-router';
import { locale as routesLocale } from '@/utils/locale';

import { billingAnalytics } from '@/analytics';

const isFree = (item) => {
  if (!item?.addons || item.addons.length === 0) return false;
  return item.addons.every(addon => Array.isArray(addon.billingcycles) && addon.billingcycles.length === 0);
};

const RetentionVpsOci = ({
  onRetentionVpsOciFollowCancel,
  getGALabel,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { subscriptionId: hostingId } = useParams();

  const { subscriptionsV2: subscriptionAnalyticsV2 } = billingAnalytics;

  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null);

  const { loading: loadingData } = useSelector(state => state.vpsOci);
  const { panels = [] } = useSelector(state => state.vpsOci.data.availableOptions) || {};

  const { status: changeImageStatus } = useSelector(state => state.vpsOci.changeImage) || {};
  const { invoiceGenerated, invoiceId: imageInvoiceId } = useSelector(state => state.vpsOci.changeImage.data) || {};

  const { billing: billingLocale } = useLocale();
  const { manageSubscription: manageSubscriptionLocale } = billingLocale;
  const {
    vpsOci: {
      retention: {
        about: {
          title: titleLocale = '',
          description: descriptionLocale = '',
        } = {},
        selectPanel: {
          title: selectPanelTitleLocale = '',
          tagFree: selectPanelTagFreeLocale = '',
        } = {},
        buttons: {
          followCancel: followCancelButtonLocale = '',
        } = {},
      } = {},
    } = {},
  } = manageSubscriptionLocale;

  const handleItemClick = useCallback((itemSlug) => {
    setSelectedItem(itemSlug);
    setShowModal(true);

    subscriptionAnalyticsV2.retentionPainelNameClickV2(itemSlug, getGALabel);
  }, [getGALabel, subscriptionAnalyticsV2]);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setModalType(null);

    dispatch(vpsOciActions.changeImage.reset());
  }, [dispatch]);

  const handleRetentionVpsOciFollowCancel = useCallback(() => {
    onRetentionVpsOciFollowCancel && onRetentionVpsOciFollowCancel();
  }, [onRetentionVpsOciFollowCancel]);

  const SkeletonRender = () => (
    <>
      <Skeleton variant="rectangle" width="100%" height="74px" />
      <Skeleton variant="rectangle" width="100%" height="74px" />
      <Skeleton variant="rectangle" width="100%" height="74px" />
      <Skeleton variant="rectangle" width="100%" height="74px" />
      <Skeleton variant="rectangle" width="100%" height="74px" />
    </>
  );

  useEffect(() => {
    dispatch(vpsOciActions.data.get({ hostingId }));
  }, [dispatch, hostingId]);

  useEffect(() => {
    if (invoiceGenerated) {
      history.push(`${routesLocale('routes.billing')}${routesLocale('routes.unpaid')}/${imageInvoiceId}?openInvoice=true`);
      return;
    }

    const status = changeImageStatus;
    if (status === 'success') {
      window.location.href = `${routesLocale('routes.products')}${routesLocale('routes.productdetail')}/${hostingId}`;
    } if (status === 'server_inactive') {
      setModalType('serverInactive');
    } else if (status === 'limit_exceeded') {
      setModalType('limitExceeded');
    } else if (status === 'not_found') {
      setModalType('notFound');
    } else if (status === 'unknown_error') {
      setModalType('unknownError');
    } else if (status === 'unknown_error_change_image') {
      setModalType('unknownErrorChangeImage');
    }
  }, [changeImageStatus, history, hostingId, imageInvoiceId, invoiceGenerated]);

  return (
    <Styles.Wrapper data-testid="vpsoci-retention-wrapper">
      <Styles.Content>
        <Styles.About>
          <Styles.Title>{titleLocale}</Styles.Title>
          <Styles.Description>{descriptionLocale}</Styles.Description>
        </Styles.About>
        <Styles.SelectPanel>
          <Styles.SelectPanelTitle>{selectPanelTitleLocale}</Styles.SelectPanelTitle>
          <Styles.SelectPanelList>
            {
              loadingData ? <SkeletonRender />
                : panels.map((item) => {
                  if (!item.slug) return null;

                  const itemName = item?.name;
                  const iconItem = getIconBySlug(item.slug);
                  const isFreeItem = isFree(item);
                  return (
                    <Styles.SelectPanelItem
                      key={item.slug}
                      onClick={() => handleItemClick(item.slug)}
                      data-testid={`vpsoci-templates-tab-item-${item.slug}`}
                    >
                      {iconItem && <Styles.SelectPanelItemIcon src={iconItem} alt={item.slug} data-testid={`vpsoci-templates-tab-item-icon-${item.slug}`} />}
                      {itemName && (
                        <Styles.SelectPanelItemName>
                          {itemName}
                          {isFreeItem && (
                          <Styles.SelectPanelItemTag>{selectPanelTagFreeLocale}</Styles.SelectPanelItemTag>
                          )}
                        </Styles.SelectPanelItemName>
                      )}
                    </Styles.SelectPanelItem>
                  );
                })
            }
          </Styles.SelectPanelList>
        </Styles.SelectPanel>
        <Styles.ActionButton>
          <Button
            testId="vpsoci-retention-follow-cancel-button"
            label={followCancelButtonLocale}
            variant="tertiary"
            size="large"
            onClick={handleRetentionVpsOciFollowCancel}
          />
        </Styles.ActionButton>
      </Styles.Content>
      <Styles.ImageWrapper>
        <Styles.Image src={SnappyImage} data-testid="vpsoci-retention-image" />
      </Styles.ImageWrapper>

      {(showModal || modalType) && (
        <ModalPanels
          onClose={handleCloseModal}
          selectedItem={selectedItem}
          modalTypeCustom={modalType}
          retentionPageInfo={{
            isRetentionCancellationPage: true,
            retentionGetGALabel: getGALabel,
          }}
        />
      )}
    </Styles.Wrapper>
  );
};

export default RetentionVpsOci;
